import { useStateWithOverwrite } from '@mnd-frontend/hooks';
import { PURPLE_700 } from '@mnd-frontend/ui';
import { removeHtmlTags } from '@mnd-frontend/utils';
import { type ClipboardEvent, type FormEvent, createRef, useCallback } from 'react';
import styled, { css } from 'styled-components';

const Span = styled.span<{ $allowNewline: boolean }>`
  display: block;
  height: 100%;

  &[data-placeholder]::before {
    content: attr(data-placeholder);
    color: white;
    pointer-events: none;
    opacity: 0.7;
  }

  &[data-placeholder]:focus::before,
  &[data-placeholder]:not(:empty)::before {
    content: none;
  }

  &:hover,
  &:active,
  &:focus,
  &:focus-within {
    box-shadow: 0 0 0 1px ${PURPLE_700}; // TODO: tmp color
  }
  ${props =>
    props.$allowNewline
      ? css`
          white-space: pre-line;
        `
      : css`
          br {
            display: none;
          }
        `}
`;

export const TextInput = ({
  initialValue,
  placeholder,
  testId,
  allowNewline,
  onChange,
}: {
  initialValue: string;
  placeholder: string;
  testId?: string;
  allowNewline?: boolean;
  onChange: (initialValue: string) => void;
}) => {
  const ref = createRef<HTMLSpanElement>();
  const [text, setText] = useStateWithOverwrite(initialValue ?? '');

  const handleFocus = useCallback(() => {
    if (ref.current) {
      const p = ref.current;
      p.focus();
      const text = p.innerText;
      if (p.innerText.length === 0) p.innerHTML = '\u00a0';
      if (p.hasChildNodes()) {
        const s = window.getSelection();
        const r = document.createRange();
        const e = p.childElementCount > 0 ? p.lastChild : p;
        if (!(s && r && e)) return;
        r.setStart(e, 1);
        r.setEnd(e, 1);
        s.removeAllRanges();
        s.addRange(r);
        p.innerText = text;
      }
    }
  }, [ref]);

  const handleBlur = () => {
    if (text !== (initialValue ?? '')) {
      onChange(text);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLSpanElement>) => {
    event.preventDefault();
    const clipboardText = event.clipboardData.getData('text');
    try {
      document.execCommand('insertHTML', false, clipboardText); // Security exception may be thrown by some browsers.
    } catch (e) {
      if (ref.current) {
        // Will replace current content
        ref.current.innerText = clipboardText;
      }
    }
  };

  const handleInput = (e: FormEvent<HTMLSpanElement>) => {
    if (!allowNewline) {
      setText(e.currentTarget.textContent ?? '');
      return;
    }
    /*
        We want to keep the first newline and replace the following newlines with space.
        This will force the content to be updated and overwritten with the value from gql,
        which does not contain the extra spaces.
      */
    const rawContent = e.currentTarget.innerHTML
      .replace(/(<br\/{0,1}>|\n)/gi, '\n')
      .replace(/\n+/g, match => {
        const maxLineBreaks = 2;
        const numNewLines = match.length;
        if (numNewLines <= maxLineBreaks) {
          return match;
        } else {
          return '\n'.repeat(maxLineBreaks) + ' '.repeat(numNewLines - maxLineBreaks);
        }
      });
    setText(removeHtmlTags(rawContent) ?? '');
  };

  return (
    <Span
      ref={ref}
      $allowNewline={!!allowNewline}
      contentEditable
      role="textbox"
      suppressContentEditableWarning
      data-placeholder={placeholder}
      date-testid={testId}
      onFocus={handleFocus}
      onPaste={handlePaste}
      onBlur={handleBlur}
      onInput={handleInput}
    >
      {initialValue}
    </Span>
  );
};
